import { useNavigate } from "react-router";
import type { AspectMode } from "@hybrid-iptv/player";
import { usePlayerStore } from "../player/playerStore.js";
import { usePlayerSettings } from "../player/playerSettingsStore.js";
import { SEEK_BACK_S, SEEK_FORWARD_S } from "../player/seekSteps.js";
import { useFavoritesStore } from "../library/favoritesStore.js";
import { dvrAvailable } from "../dvr/dvr.js";
import { runRemoteAction, useRemoteActions } from "./remoteActions.js";
import { showRemoteNote } from "./remoteNote.js";
import { shiftLiveBy } from "../player/liveShift.js";
import { SLEEP_CHOICES, useSleep } from "../player/sleepTimer.js";
import { recordViewerChoice } from "../player/trackMemory.js";

const ASPECTS: AspectMode[] = ["fit", "fill", "stretch", "zoom"];
const ASPECT_WORDS: Record<AspectMode, string> = {
  fit: "Fit (whole picture)",
  fill: "Fill (zoomed to the screen)",
  stretch: "Stretch (to the screen's shape)",
  zoom: "Zoom (crops black bars in the picture)",
};

/**
 * The remote actions that belong to no one screen: going to a page, and the
 * player functions that live in its store. Mounted once, in the app shell.
 */
export function useRemoteAppActions(): void {
  const navigate = useNavigate();

  /** A page from full screen: leave full screen first, or the picture stays over it. */
  const go = (path: string) => () => {
    runRemoteAction("exitFullscreen");
    navigate(path);
  };

  const hasSource = () => usePlayerStore.getState().source !== null;
  const player = () => usePlayerStore.getState().player;

  /**
   * Seeking needs a timeline: films, episodes and catch-up, and live TV while
   * its timeshift buffer holds something behind the playhead.
   */
  const seekBy = (seconds: number) => () => {
    const { source, currentTime, duration, seek } = usePlayerStore.getState();
    if (!source) return false;
    if (source.type === "live") {
      showRemoteNote(shiftLiveBy(player(), seconds));
      return;
    }
    if (!Number.isFinite(duration) || duration <= 0) {
      showRemoteNote("This one can't skip yet");
      return;
    }
    seek(Math.min(Math.max(0, currentTime + seconds), duration));
    showRemoteNote(seconds < 0 ? `⏪ ${-seconds} seconds back` : `⏩ ${seconds} seconds forward`);
  };

  useRemoteActions({
    home: go("/"),
    live: go("/live"),
    guide: go("/live"),
    movies: go("/movies"),
    series: go("/series"),
    mylist: go("/mylist"),
    catchup: go("/catchup"),
    recordings: () => {
      if (!dvrAvailable()) return false;
      go("/recordings")();
    },
    search: go("/search"),
    settings: go("/settings"),
    // Menu outside full screen (the player answers it while watching): the app's
    // Settings — it did nothing before (owner, 2026-10-10).
    playerSettings: go("/settings"),

    playPause: () => {
      if (!hasSource()) return false;
      void usePlayerStore.getState().togglePlayPause();
    },
    stop: () => {
      if (!hasSource()) return false;
      usePlayerStore.getState().close();
    },
    mute: () => {
      if (!hasSource()) return false;
      usePlayerStore.getState().toggleMute();
      showRemoteNote(usePlayerStore.getState().muted ? "🔇 Sound off" : "🔊 Sound on");
    },
    sleepTimer: () => {
      if (!hasSource()) return false;
      // Each press goes to the next length; past the longest, off.
      const { endsAt, setSleep } = useSleep.getState();
      const left = endsAt ? Math.round((endsAt - Date.now()) / 60_000) : 0;
      const next = SLEEP_CHOICES.find((minutes) => minutes > left + 1) ?? null;
      setSleep(next);
      showRemoteNote(next ? `Sleep timer: ${next} minutes` : "Sleep timer off");
    },
    seekBack: seekBy(-SEEK_BACK_S),
    seekForward: seekBy(SEEK_FORWARD_S),
    aspect: () => {
      if (!hasSource()) return false;
      const { aspect, setAspect } = usePlayerSettings.getState();
      const next = ASPECTS[(ASPECTS.indexOf(aspect) + 1) % ASPECTS.length]!;
      setAspect(next);
      player()?.setAspectMode?.(next);
      // On a widescreen channel the three look alike: the words say which it is.
      showRemoteNote(`Picture size: ${ASPECT_WORDS[next]}`);
    },
    subtitles: () => {
      const engine = player();
      if (!hasSource() || !engine?.tracks || !engine.selectTrack) return false;
      void engine.tracks().then((tracks) => {
        const text = tracks.filter((track) => track.kind === "text");
        if (text.length === 0) {
          showRemoteNote("No subtitles on this one");
          return;
        }
        const on = text.some((track) => track.selected);
        recordViewerChoice(tracks, "text", on ? null : text[0]!.id);
        engine.selectTrack?.("text", on ? null : text[0]!.id);
        showRemoteNote(on ? "Subtitles off" : `Subtitles on: ${text[0]!.label}`);
      });
    },
    audioTrack: () => {
      const engine = player();
      if (!hasSource() || !engine?.tracks || !engine.selectTrack) return false;
      void engine.tracks().then((tracks) => {
        const audio = tracks.filter((track) => track.kind === "audio");
        if (audio.length < 2) {
          showRemoteNote("Only one audio track on this one");
          return;
        }
        const at = audio.findIndex((track) => track.selected);
        const next = (at + 1) % audio.length;
        recordViewerChoice(tracks, "audio", audio[next]!.id);
        engine.selectTrack?.("audio", audio[next]!.id);
        showRemoteNote(`Audio ${next + 1} of ${audio.length}: ${audio[next]!.label}`);
      });
    },
    favorite: () => {
      // The live channel playing is the head of the recent list (pushRecent on every tune).
      const { source, recentChannels } = usePlayerStore.getState();
      const channel = source?.type === "live" ? recentChannels.find((c) => c.name === source.title) : undefined;
      if (!channel) {
        if (source) showRemoteNote("Favourites from the remote are for live channels");
        return source ? undefined : false;
      }
      const was = useFavoritesStore.getState().isFavorite("channel", channel.id);
      useFavoritesStore.getState().toggleFavorite("channel", channel.id);
      showRemoteNote(was ? `Removed ${channel.name} from favourites` : `★ Added ${channel.name} to favourites`);
    },
  });
}

